import type { BalanceDay, PersonalPenalty, Translator } from "types";
import requestWithAuth from "../httpClient";
import { rootURL } from "../rootURL";

// Shape returned by GET /translators with clients populated (runtime differs
// from the global Translator type, whose `clients` is an array of ObjectId).
export type PopulatedTranslator = {
	_id: string;
	name: string;
	surname: string;
	// A formatted UTC string, not a Date: everything that writes this field
	// sends getMomentUTC().format().
	suspended?: { status: boolean; time?: string };
	email?: string;
	wantsToReceiveEmails?: boolean;
	card?: string;
	clients: Array<{
		_id: string;
		name: string;
		surname: string;
		suspended: boolean;
		suspendedTranslators: string[];
	}>;
};

const translatorsURL = `${rootURL}translators/`;
const balanceDayURL = `${rootURL}balance-day/`;
const personalPenaltyURL = `${rootURL}personal-penalty/`;

export function getTranslators({
	searchQuery = "",
	shouldGetClients = false,
	hideSuspended = false,
}) {
	let queryParams = "";
	if (searchQuery) {
		queryParams += `${queryParams ? "&" : "?"}searchQuery=${encodeURIComponent(searchQuery)}`;
	}
	// let the backend drop suspended translators so the payload stays small
	if (hideSuspended) {
		queryParams += `${queryParams ? "&" : "?"}hideSuspended=true`;
	}
	// keep ability to include clients, but default false for perf
	if (shouldGetClients) {
		queryParams += `${queryParams ? "&" : "?"}shouldGetClients=true`;
		// select clients as well so populated data returns
		queryParams += `${queryParams ? "&" : "?"}select=name,surname,_id,suspended,email,wantsToReceiveEmails,clients,card`;
	} else {
		// request only lightweight fields but include email preferences for UI
		queryParams += `${queryParams ? "&" : "?"}select=name,surname,_id,suspended,email,wantsToReceiveEmails,card`;
	}
	// Default select fields for lightweight translator list
	if (!queryParams.includes("select=")) {
		queryParams += `${queryParams ? "&" : "?"}select=name,surname,_id,suspended,email,wantsToReceiveEmails,card`;
	}
	return requestWithAuth<PopulatedTranslator[]>(
		"get",
		`${translatorsURL}get/${queryParams}`,
	);
}

export function addTranslator(translator: Translator) {
	return requestWithAuth("post", `${translatorsURL}add/`).send(translator);
}

export function removeTranslator(id: string) {
	return requestWithAuth("delete", `${translatorsURL}${id}`);
}

/**
 * Accepts the populated shape too. The translators page edits a translator
 * straight out of the cache, where `clients` holds whole documents rather than
 * ids; mongoose casts those to their _id on the way in, so the two shapes are
 * interchangeable here even though they do not look it.
 */
export function updateTranslator(translator: Translator | PopulatedTranslator) {
	return requestWithAuth("put", `${translatorsURL}${translator._id}`).send(
		translator,
	);
}

export function assignClientToTranslatorRequest({
	clientId,
	translatorId,
}: {
	clientId: string;
	translatorId: string;
}) {
	return requestWithAuth("put", `${translatorsURL}assign-client`).send({
		clientId,
		translatorId,
	});
}

export function sendNotificationEmailsRequest(date?: string) {
	const dateParam = date ? `?date=${encodeURIComponent(date)}` : "";
	return requestWithAuth("get", `${translatorsURL}send-emails${dateParam}`);
}

export type MailoutPreview = {
	// Start of the report day in UTC. When no date is requested the server
	// resolves it to the most recent day that has balance days.
	date: string;
	total: number;
	recipientCount: number;
	translators: Array<{ label: string; total: number }>;
};

export function getMailoutPreviewRequest(date?: string) {
	const dateParam = date ? `?date=${encodeURIComponent(date)}` : "";
	return requestWithAuth<MailoutPreview>(
		"get",
		`${translatorsURL}mailout-preview${dateParam}`,
	);
}

// Answers with a date string, or a Russian sentence saying there are no gifts.
// The card prints whichever it gets.
export function sendLastVirtualGiftDateRequest(id: string) {
	return requestWithAuth<string>("get", `${translatorsURL}last-gift/${id}`);
}

export function getBalanceDay({
	translatorId,
	clientId,
	dateTimeId,
}: {
	translatorId: string;
	clientId: string;
	dateTimeId: string;
}) {
	return requestWithAuth<BalanceDay>(
		"get",
		`${balanceDayURL}?translatorId=${translatorId}&clientId=${clientId}&dateTimeId=${encodeURIComponent(
			dateTimeId,
		)}`,
	);
}

export function getAutoGeneratedBalanceDayRequest({
	translatorId,
	clientId,
	dateTimeId,
}: {
	translatorId: string;
	clientId: string;
	dateTimeId: string;
}) {
	return requestWithAuth<BalanceDay>(
		"get",
		`${balanceDayURL}auto-generated/?translatorId=${translatorId}&clientId=${clientId}&dateTimeId=${encodeURIComponent(
			dateTimeId,
		)}`,
	);
}

export function createBalanceDay({
	newBalanceDay,
}: {
	newBalanceDay: BalanceDay;
}) {
	return requestWithAuth("post", `${balanceDayURL}create`).send({
		...newBalanceDay,
	});
}

export function updateBalanceDay({
	balanceDayToSubmit,
}: {
	balanceDayToSubmit: BalanceDay;
}) {
	return requestWithAuth("put", `${balanceDayURL}update`).send({
		...balanceDayToSubmit,
	});
}

export function getBalanceDaysForTranslatorRequest({
	dateTimeFilter = "",
	translatorId = "",
}) {
	return requestWithAuth(
		"get",
		`${balanceDayURL}translators?dateTimeFilter=${dateTimeFilter}&translatorId=${translatorId}`,
	);
}

export function getPenaltiesForTranslatorRequest({
	dateTimeFilter = "",
	translatorId = "",
}) {
	return requestWithAuth(
		"get",
		`${personalPenaltyURL}get?dateTimeFilter=${dateTimeFilter}&translatorId=${translatorId}`,
	);
}

// Penalties for many translators in one request, keyed by translator id.
// Every requested id is present in the response, empty list included.
export function getPenaltiesForTranslatorsRequest({
	dateTimeFilter = "",
	translatorIds = [] as string[],
}) {
	return requestWithAuth<Record<string, PersonalPenalty[]>>(
		"get",
		`${personalPenaltyURL}get-batch?dateTimeFilter=${encodeURIComponent(
			dateTimeFilter,
		)}&translatorIds=${translatorIds.join(",")}`,
	);
}

export async function createPersonalPenalty({
	personalPenaltyData,
}: {
	personalPenaltyData: PersonalPenalty;
}) {
	return requestWithAuth("post", `${personalPenaltyURL}create`).send(
		personalPenaltyData,
	);
}

export async function toggleClientSuspendedRequest({
	clientId,
	translatorId,
}: {
	clientId: string;
	translatorId: string;
}) {
	return requestWithAuth("put", `${translatorsURL}suspend-client`).send({
		clientId,
		translatorId,
	});
}

export function getTranslatorMetricsRequest({
	translatorId,
	date,
}: {
	translatorId: string;
	date?: string;
}) {
	const dateParam = date ? `&date=${encodeURIComponent(date)}` : "";
	return requestWithAuth(
		"get",
		`${balanceDayURL}translator-metrics?translatorId=${translatorId}${dateParam}`,
	);
}

export type TranslatorMetrics = {
	translatorId: string;
	currentMonthTotal: number;
	previousMonthTotal: number;
	middleMonth: number;
	yesterdayPerClient: Record<string, number>;
};

// Metrics for many translators in one request. Replaces the per-card
// translator-metrics call the translators page used to make.
export function getTranslatorsMetricsRequest({
	translatorIds,
	date,
}: {
	translatorIds: string[];
	date?: string;
}) {
	const dateParam = date ? `&date=${encodeURIComponent(date)}` : "";
	return requestWithAuth<{ data: TranslatorMetrics[] }>(
		"get",
		`${balanceDayURL}translators-metrics?translatorIds=${translatorIds.join(
			",",
		)}${dateParam}`,
	);
}
